import { differenceInDays } from "date-fns";
import { cloneDeep, keyBy, round, sumBy } from "lodash";
import moment from "moment";

import type {
  Forecast_me_mmm_forecast_forecastedContributionsByKpi,
  Forecast_me_mmm_forecast_prediction,
  MMMForecastDataInput,
} from "@north-beam/nb-client-side";

import { ISO_DATE_FORMAT } from "@north-beam/nb-common";

import { forecastedContributionsByKpiToMetadata } from "../components/explore-budgets/scenario-modeling/forecast-chart";
import {
  type BudgetMixInput,
  type SpendDatum,
  type WeeklyDatum,
  type AggregatedData,
} from "../components/explore-budgets/scenario-modeling/types";

import { getDate } from "./formatters";
import {
  addDaysToIsoDate,
  getAllDaysInDateRange,
  getEndDateExclusiveWeeksForDateRange,
} from "./get_cycle_weeks";
import { NbSet } from "./nb-set";

class MMMDataShapeTransformationError extends Error {
  debugMessage: string;
  clientErrorMessage: string;
  constructor(debugMessage: string, clientErrorMessage: string) {
    super(debugMessage);
    this.debugMessage = debugMessage;
    this.clientErrorMessage = clientErrorMessage;
  }
}

class DateNotFoundInDataError extends MMMDataShapeTransformationError {
  constructor(date: string, data: Record<string, any>) {
    const message = `Date ${date} not found in data ${JSON.stringify(data)}`;
    super(message, "No data for specified date");
  }
}

class DateOutOfBoundsError extends MMMDataShapeTransformationError {
  constructor(
    date: string,
    dateRange: { start_date: string; end_date: string },
  ) {
    const message = `Date ${date} out of bounds for date range ${JSON.stringify(
      dateRange,
    )}`;
    super(message, "Invalid date");
  }
}

type KpiRangedData = {
  kpi_id: string;
  values: {
    low: number;
    median: number;
    high: number;
  };
};

export type BudgetMixCompatibleWeeklySpendData = {
  start_date: string;
  end_date: string;
  spend_data: SpendDatum[];
};

export type ForecastSpendDataInput = {
  date: string; // NOTE: Start date
  data: Record<string, number>;
};

type ForecastPrediction = {
  date: string;
  kpis: Record<
    string,
    {
      high: number;
      low: number;
      value: number;
    }
  >;
};

/**
 * @description
 * Converts weekly spend data formatted for a Budget Mix  into a list of forecast
 * spend data used to generate a forecast.
 *
 * @param {BudgetMixCompatibleWeeklySpendData[]} weeklyData
 *   Budget mix weekly spend data
 * @returns {ForecastSpendDataInput[]}
 *   A list of forecast spend data inputs, where each input is an object with
 *   a date and a data object. The date is the start date of the week, and
 *   the data object has keys for each data column name and values for the
 *   spend data for that column.
 */
export function weeklySpendToForecastSpend(
  weeklyData: BudgetMixCompatibleWeeklySpendData[],
): ForecastSpendDataInput[] {
  return weeklyData.map((weeklyDatum) => ({
    date: weeklyDatum.start_date,
    data: Object.fromEntries(
      weeklyDatum.spend_data.map((spendDatum) => [
        spendDatum.data_column_name,
        spendDatum.spend,
      ]),
    ),
  }));
}

/**
 * @description
 * Converts a list of forecast spend data inputs into weekly spend data formatted
 * for a Budget Mix.
 *
 * @param {ForecastSpendDataInput[]} forecastData
 *   A list of forecast spend data inputs, where each input is an object with
 *   a date and a data object. The date is the start date of the week, and
 *   the data object has keys for each data column name and values for the
 *   spend data for that column.
 * @returns {BudgetMixCompatibleWeeklySpendData[]}
 *   A list of BudgetMix-formatted weekly spend data, where each
 *   element is an object with start_date, EXCLUSIVE end_date, and spend_data properties.
 */
export function forecastSpendToWeeklySpend(
  forecastData: ForecastSpendDataInput[],
): BudgetMixCompatibleWeeklySpendData[] {
  return forecastData.map((forecastDatum) => {
    const startDate = moment(forecastDatum.date);
    // NOTE: End date is exclusive
    const endDate = startDate.clone().add(7, "days");
    return {
      start_date: startDate.format(ISO_DATE_FORMAT),
      end_date: endDate.format(ISO_DATE_FORMAT),
      spend_data: Object.entries(forecastDatum.data).map(
        ([dataColumnName, spend]) => ({
          data_column_name: dataColumnName,
          spend: spend as number,
        }),
      ),
    };
  });
}

/**
 * @description
 * Converts a given cycle spend into a list of weekly budget mix spend data.
 *
 * @param {Record<string, number>} cycleSpend
 *   Spend for entire cycle formatted as { dataColumnName: spendValue }
 * @param {{ start_date: string; end_date: string }} dateRange
 *   The date range of the cycle (MUST BE INCLUSIVE DATE)
 * @returns {BudgetMixCompatibleWeeklySpendData[]}
 *   Budget mix weekly spend data without the KPI data
 */
export function cycleSpendToWeeklySpend(
  cycleSpend: Record<string, number>,
  dateRange: { start_date: string; end_date: string },
): BudgetMixCompatibleWeeklySpendData[] {
  // NOTE: Budget mixes, due to their data being generated by
  // the Data Science team, have exclusive end-dates on their
  // weeks.
  const weeks = getEndDateExclusiveWeeksForDateRange(dateRange);

  const weeklySpends = Object.entries(cycleSpend).map(
    ([dataColumnName, spend]) => ({
      data_column_name: dataColumnName,
      spend: spend / weeks.length,
    }),
  );

  return weeks.map((week) => {
    return {
      start_date: week.startDate,
      end_date: week.endDate,
      spend_data: [...weeklySpends],
    };
  });
}

/**
 * Converts a cycle spend data formatted as {dataColumnName: spendValue}
 * into a list of forecast spend data used to generate a forecast.
 *
 * @param cycleSpend - The cycle spend data, where the keys are the data column names,
 *                     and the values are the total spend for that data column in the
 *                     cycle.
 * @param dateRange - The range of dates for the cycle. The end date must be INCLUSIVE
 * @returns A list of forecast spend data, where each element represents a week of
 *          spend data. Formatted as {date: startDateString, data: {dataColumName: spendValue}}
 */
export function cycleSpendToForecastInput(
  cycleSpend: Record<string, number>,
  dateRange: { start_date: string; end_date: string },
): ForecastSpendDataInput[] {
  return weeklySpendToForecastSpend(
    cycleSpendToWeeklySpend(cycleSpend, dateRange),
  );
}

/**
 * Merges budget spend and actual spend up to a certain date to create a desired
 * forecast spend. The budget spend is spread out evenly across the remaining days
 * in the cycle.
 *
 * @param cycleSpend
 *   The total spend for each data column in the cycle.
 * @param weeklyActualSpendToDate
 *   The actual spend for each data column up to the reference date.
 * @param referenceDate
 *   The date up to which the actual spend data is available. Leftover budget
 *   spend will start the day AFTER the referenceDate. The referenceDate cannot
 *   be after or equal to the dateRange end_date
 * @param dateRange
 *   The date range of the cycle. The end date must be INCLUSIVE.
 * @returns
 *   The desired forecast spend in the format of a list of objects, each
 *   representing a week of spend data. Formatted as
 *   {date: startDateString, data: {dataColumName: spendValue}}
 */
export function mergeBudgetAndActualSpendToForecastInput(
  cycleSpend: { [dataColumnName: string]: number },
  weeklyActualSpendToDate: {
    [dataColumnName: string]: {
      actual: number;
      dateRange: { startDate: string; endDate: string };
    }[];
  },
  referenceDate: string,
  dateRange: { start_date: string; end_date: string },
): ForecastSpendDataInput[] {
  if (getDate(dateRange.end_date) <= getDate(referenceDate)) {
    throw new DateOutOfBoundsError(referenceDate, dateRange);
  }

  const actualSpendsByStartDate = Object.entries(
    weeklyActualSpendToDate,
  ).reduce((agg, curr) => {
    const [dataColumnName, actualSpendData] = curr;
    actualSpendData.forEach(({ actual, dateRange }) => {
      if (agg[dateRange.startDate]) {
        agg[dateRange.startDate][dataColumnName] = actual;
      } else {
        agg[dateRange.startDate] = { [dataColumnName]: actual };
      }
    });
    return agg;
  }, {} as { [startDate: string]: { [dataColumnName: string]: number } });

  const weeks = getEndDateExclusiveWeeksForDateRange(dateRange);

  // Note: We will start spending the day after the reference date
  const daysRemainingFromReferenceDate = differenceInDays(
    getDate(dateRange.end_date).toDate(),
    getDate(referenceDate).toDate(),
  );

  const allDaysFromReferenceDateToEndDate = new NbSet(
    getAllDaysInDateRange({
      start_date: addDaysToIsoDate(referenceDate, 1),
      end_date: dateRange.end_date,
    }),
  );

  const aggregatedActualSpends = Object.entries(weeklyActualSpendToDate).reduce(
    (agg, [dataColumnName, actualSpendData]) => {
      agg[dataColumnName] = sumBy(
        actualSpendData,
        (actualSpendDatum) => actualSpendDatum.actual,
      );
      return agg;
    },
    {} as { [dataColumnName: string]: number },
  );

  const cycleSpendRemaining = Object.entries(cycleSpend).reduce(
    (agg, [dataColumnName, spend]) => {
      agg[dataColumnName] = Math.max(
        spend - (aggregatedActualSpends[dataColumnName] ?? 0),
        0,
      );
      return agg;
    },
    {} as { [dataColumnName: string]: number },
  );

  const cycleSpendToAllocatePerDayPastReferenceDate = Object.entries(
    cycleSpendRemaining,
  ).reduce((agg, [dataColumnName, spend]) => {
    agg[dataColumnName] =
      daysRemainingFromReferenceDate === 0
        ? 0
        : spend / daysRemainingFromReferenceDate;
    return agg;
  }, {} as { [dataColumnName: string]: number });

  const formattedActualWeeklySpends: BudgetMixCompatibleWeeklySpendData[] =
    weeks.map((week) => {
      const actualSpendsForWeek = actualSpendsByStartDate[week.startDate];

      if (!actualSpendsForWeek) {
        throw new DateNotFoundInDataError(
          week.startDate,
          actualSpendsByStartDate,
        );
      }

      const weeklySpends = Object.entries(actualSpendsForWeek).map(
        ([dataColumnName, spend]) => ({
          data_column_name: dataColumnName,
          spend: spend,
        }),
      );

      return {
        start_date: week.startDate,
        end_date: week.endDate,
        spend_data: [...weeklySpends],
      };
    });

  const allDataColumnsInCycleSpend = new NbSet(Object.keys(cycleSpend));

  const formattedDesiredWeeklySpends: BudgetMixCompatibleWeeklySpendData[] =
    formattedActualWeeklySpends.map((actualWeeklySpend) => {
      const { start_date, end_date, spend_data } = actualWeeklySpend;
      if (getDate(end_date) < getDate(referenceDate)) {
        const newSpendData = cloneDeep(spend_data);
        const dataColumnsInData = new NbSet(
          newSpendData.map((datum) => datum.data_column_name),
        );
        const zeroSpendDataColumnsToAdd =
          allDataColumnsInCycleSpend.difference(dataColumnsInData);

        zeroSpendDataColumnsToAdd.forEach((dataColumnName) => {
          newSpendData.push({ data_column_name: dataColumnName, spend: 0 });
        });

        return {
          start_date,
          end_date,
          spend_data: newSpendData,
        };
      }

      const dataColumnsUpdated = new NbSet<string>([]);

      // These weeks are end-date-exclusive because data science team wanted
      // them that way, so subtract 1 day to end date for this logic
      const weekDateRange = {
        start_date,
        end_date: addDaysToIsoDate(end_date, -1),
      };
      const daysInWeekDateRange = new NbSet(
        getAllDaysInDateRange(weekDateRange),
      );
      const numberOfDaysToAllocateSpend =
        allDaysFromReferenceDateToEndDate.intersection(
          daysInWeekDateRange,
        ).size;

      const updatedSpendData = spend_data.map((spendDatum) => {
        dataColumnsUpdated.add(spendDatum.data_column_name);
        return {
          data_column_name: spendDatum.data_column_name,
          spend: round(
            spendDatum.spend +
              cycleSpendToAllocatePerDayPastReferenceDate[
                spendDatum.data_column_name
              ] *
                numberOfDaysToAllocateSpend,
            4,
          ),
        };
      });
      const leftoverSpendColumns =
        allDataColumnsInCycleSpend.difference(dataColumnsUpdated);
      leftoverSpendColumns.forEach((dataColumnName) => {
        updatedSpendData.push({
          data_column_name: dataColumnName,
          spend: round(
            cycleSpendToAllocatePerDayPastReferenceDate[dataColumnName] *
              numberOfDaysToAllocateSpend,
            4,
          ),
        });
      });
      return {
        start_date,
        end_date,
        spend_data: updatedSpendData,
      };
    });

  return weeklySpendToForecastSpend(formattedDesiredWeeklySpends);
}

export function budgetMixSpendToTotalSpend(spendData: SpendDatum[]) {
  return sumBy(spendData, (spendDatum) => spendDatum.spend);
}

export function forecastSpendToTotalSpend(
  forecastData: ForecastSpendDataInput[],
): number {
  return budgetMixSpendToTotalSpend(
    budgetMixWeeklySpendToSummarySpend(
      forecastSpendToWeeklySpend(forecastData),
    ),
  );
}

/**
 * Converts a list of forecasted predictions into a record where the keys
 * are the start dates of weeks and the values are records where the keys
 * are kpi names and the values are the kpi data for that kpi formatted as
 * needed by a budget mix.
 *
 * This is done in aggregate to ensure good runtime performance when generating
 * a full budget mix.
 *
 * @param {ForecastPrediction[]} forecastedPredictions
 * @param {Record<string, { id: string }>} kpisByName
 * @returns {Record<string, Record<string, KpiRangedData>>}
 */
export function getBudgetMixKpisByStartDate(
  forecastedPredictions: ForecastPrediction[],
  kpisByName: Record<string, { id: number | string }>,
): Record<string, Record<string, KpiRangedData>> {
  const forecastsByDate = forecastedPredictions.reduce((agg, prediction) => {
    if (agg[prediction.date]) {
      agg[prediction.date].push(prediction);
    } else {
      agg[prediction.date] = [prediction];
    }
    return agg;
  }, {} as Record<string, ForecastPrediction[]>);
  const kpiDataByDate: Record<string, Record<string, KpiRangedData>> = {};

  Object.entries(forecastsByDate).forEach(([date, forecasts]) => {
    kpiDataByDate[date] = forecastedKpisToBudgetMixKpis(forecasts, kpisByName);
  });

  return kpiDataByDate;
}

/**
 * Convert forecast predictions to budget mix kpi data.
 *
 * Note: Assumes all forecastPredictions are the same date and there are no duplicate KPI names.
 *
 * @param {ForecastPrediction[]} forecastPredictions - The forecast predictions to process
 * @param {Record<string, { id: string }>} kpisByName - Map of kpi names to their corresponding ids
 * @returns {Record<string, KpiRangedData>} - A map of kpi names to their corresponding budget mix kpi data
 */
export function forecastedKpisToBudgetMixKpis(
  forecastPredictions: ForecastPrediction[],
  kpisByName: Record<string, { id: number | string }>,
): Record<string, KpiRangedData> {
  // NOTE: Assumes all forecastPredictions are the same date and there are no duplicate KPI names
  return forecastPredictions.reduce((budgetMixKpiData, prediction) => {
    Object.entries(prediction.kpis).forEach(([kpiName, kpiData]) => {
      if (!kpisByName[kpiName]) {
        throw new Error(
          `Cannot find kpi ${kpiName} in KPIs ${JSON.stringify(
            kpisByName,
          )} but required by forecast prediction`,
        );
      }
      if (budgetMixKpiData[kpiName]) {
        throw new Error(
          `Prediction ${JSON.stringify(
            forecastPredictions,
          )} has multiple entries for kpi ${kpiName}`,
        );
      }
      budgetMixKpiData[kpiName] = {
        values: {
          low: kpiData.low,
          median: kpiData.value,
          high: kpiData.high,
        },
        kpi_id: String(kpisByName[kpiName].id),
      };
    });
    return budgetMixKpiData;
  }, {} as Record<string, KpiRangedData>);
}

/**
 * Takes a collection of weekly budget mix data and returns the total spend
 * for each channel.
 *
 * @param weeklyData - An array of weekly budget mix data
 * @returns An array of spend data with the total spend for each channel in the format
 * { data_column_name: string, spend: number }
 */
export function budgetMixWeeklySpendToSummarySpend(
  weeklyData: BudgetMixCompatibleWeeklySpendData[],
): SpendDatum[] {
  const spendDataByDataColumnName: Record<string, SpendDatum> = cloneDeep(
    weeklyData,
  ).reduce((mappedSpendData, weeklyDatum) => {
    weeklyDatum.spend_data.forEach((spendDatum) => {
      if (mappedSpendData[spendDatum.data_column_name]) {
        mappedSpendData[spendDatum.data_column_name].spend += spendDatum.spend;
      } else {
        mappedSpendData[spendDatum.data_column_name] = spendDatum;
      }
    });
    return mappedSpendData;
  }, {} as Record<string, SpendDatum>);

  return Object.values(spendDataByDataColumnName);
}

/**
 * This function takes a collection of weekly budget mix data and returns
 * the total for each kpi for all the weeks. It assumes that the kpi names
 * are the same for all the weeks. This function does not check if the kpi
 * names are the same for all the weeks.
 *
 * @param weeklyData - An array of weekly budget mix data
 * @returns An object with the total for each kpi, keyed by the kpi_name
 */
export function budgetMixWeeklyKpisToSummaryKpis(
  weeklyData: WeeklyDatum[],
): Record<string, KpiRangedData> {
  return cloneDeep(weeklyData).reduce((aggregatedKpiData, weeklyDatum) => {
    Object.entries(weeklyDatum.summary_data.kpi_data).forEach(
      ([kpiName, kpiData]) => {
        if (aggregatedKpiData[kpiName]) {
          aggregatedKpiData[kpiName].values.low += kpiData.values.low;
          aggregatedKpiData[kpiName].values.median += kpiData.values.median;
          aggregatedKpiData[kpiName].values.high += kpiData.values.high;
        } else {
          aggregatedKpiData[kpiName] = kpiData;
        }
      },
    );
    return aggregatedKpiData;
  }, {} as Record<string, KpiRangedData>);
}

/**
 * Takes a collection of weekly budget mix data and returns the total for
 * each channel and kpi. The weekly data is assumed to have the same kpi
 * names for all the weeks. This function does not check if the kpi names
 * are the same for all the weeks.
 *
 * @param weeklyData - An array of weekly budget mix data
 * @returns An object with the total spend and kpi data for all the weeks
 */
export function budgetMixWeeklyToSummaryData(
  weeklyData: WeeklyDatum[],
): AggregatedData {
  return {
    spend_data: budgetMixWeeklySpendToSummarySpend(
      weeklyData as BudgetMixCompatibleWeeklySpendData[],
    ),
    summary_data: {
      kpi_data: budgetMixWeeklyKpisToSummaryKpis(weeklyData),
      spend: budgetMixSpendToTotalSpend(
        weeklyData.flatMap((weeklyDatum) => weeklyDatum.spend_data),
      ),
    },
  };
}

/**
 * Transforms forecast data into a budget mix input format.
 *
 * @param forecastSpend - The forecast data in the MMMForecastDataInput format.
 * @param predictions - The MMM forecast predictions.
 * @param kpis - The KPIs to include in the budget mix.
 * @param chosenKpi - The currently selected KPI.
 * @param selectedCycleDateRange - The date range of the selected cycle- MUST have INCLUSIVE end date.
 *
 * @returns The transformed budget mix data for creation.
 *
 * @throws An error if the number of weeks in the forecast data does not match the number of weeks in the selected cycle.
 * @throws An error if the date range of the forecast data does not match the date range of the selected cycle.
 */
export function forecastToBudgetMix(
  forecastSpend: MMMForecastDataInput[],
  predictions: Omit<Forecast_me_mmm_forecast_prediction, "__typename">[],
  kpis: { kpi_name: string; id: number | string }[],
  chosenKpi: { kpi_name: string },
  selectedCycleDateRange: { start_date: string; end_date: string },
  forecastedContributions:
    | Forecast_me_mmm_forecast_forecastedContributionsByKpi[]
    | null,
): BudgetMixInput {
  // Note: Prediction date is start_date of week
  const kpisByName = keyBy(kpis, "kpi_name");
  const expectedCycleWeeks = getEndDateExclusiveWeeksForDateRange(
    selectedCycleDateRange,
  );

  const weeklySpendData = forecastSpendToWeeklySpend(forecastSpend);
  const keyedWeeklyPredictionData = getBudgetMixKpisByStartDate(
    predictions,
    kpisByName,
  );
  const weeklyData: WeeklyDatum[] = weeklySpendData.map((weeklySpendDatum) => {
    return {
      ...weeklySpendDatum,
      summary_data: {
        spend: budgetMixSpendToTotalSpend(weeklySpendDatum.spend_data),
        kpi_data: keyedWeeklyPredictionData[weeklySpendDatum.start_date],
      },
    };
  });

  if (weeklyData.length !== expectedCycleWeeks.length) {
    throw new Error(
      `Received ${weeklyData.length} weeks of data instead of the expected ${expectedCycleWeeks.length} weeks of data`,
    );
  }

  const weeklyDataFailedToMatchExpectedWeeks = weeklyData
    .map(
      (weekData, i) =>
        expectedCycleWeeks[i] &&
        expectedCycleWeeks[i].startDate === weekData.start_date &&
        expectedCycleWeeks[i].endDate === weekData.end_date,
    )
    .some((didMatch) => !didMatch);

  if (weeklyDataFailedToMatchExpectedWeeks) {
    throw new Error(
      `Expected cycle date ranges ${expectedCycleWeeks} to match data date range ${weeklyData.map(
        ({ start_date, end_date }) => ({ start_date, end_date }),
      )}`,
    );
  }

  return {
    weekly_data: weeklyData,
    aggregated_data: budgetMixWeeklyToSummaryData(weeklyData),
    kpi_name: chosenKpi.kpi_name,
    create_budget_recommendations_metadata: {
      contribution_data: forecastedContributionsByKpiToMetadata(
        forecastedContributions,
      ),
    },
  };
}
